/**
 * Default input hints for person and company sub-fields.
 *
 * A plain field's placeholder and help text are authored — by the admin, or suggested by the
 * AI. A sub-field's are not: the slots are a fixed, known set, so their hints are entirely
 * predictable. The builder deliberately offers no inputs for them on a sub-field.
 *
 * These are resolved at RENDER time rather than written to every row. Persisting a constant on
 * every person/company child would be redundant, would need a migration to change a typo, and
 * would leave older rows blank forever. Resolving means one table, and editing it updates every
 * template at once.
 *
 * Keyed by plain strings on purpose. `SubKind`/`CompanySubKind` are declared twice — once in
 * the builder (`docx-builder/selection/types.ts`) and once in the fill flow
 * (`docx-generate/utils.ts`) — and this module is imported by both, so binding to either
 * union would force one of them to depend on the other. Dependency-free by design.
 */

/** Person slots. `signature` is absent: it is a location anchor and is never filled in. */
const PERSON_HINTS: Record<string, { placeholder?: string; helpText: string }> = {
  name: {
    placeholder: 'e.g. Jane Doe',
    helpText: 'Full legal name, exactly as it should appear in the document.',
  },
  role: {
    placeholder: 'e.g. Chief Executive Officer',
    helpText: 'Their job title or position for this agreement.',
  },
  company: {
    placeholder: 'e.g. Acme Holdings AG',
    helpText: 'The organisation this person represents.',
  },
  email: {
    placeholder: 'name@example.com',
    helpText: 'Used to send them the document for signing.',
  },
  phone: {
    placeholder: 'e.g. +41 44 123 45 67',
    helpText: 'Include the country code.',
  },
  address: {
    placeholder: 'e.g. Bahnhofstrasse 1, 8001 Zurich',
    helpText: 'Full postal address, including postcode and city.',
  },
};

/** Company slots. A company never signs, so there is no signature slot. */
const COMPANY_HINTS: Record<string, { placeholder?: string; helpText: string }> = {
  name: {
    placeholder: 'e.g. Acme Holdings AG',
    helpText: 'Registered company name, exactly as it should appear in the document.',
  },
  address: {
    placeholder: 'e.g. Bahnhofstrasse 1, 8001 Zurich',
    helpText: 'Registered office address, including postcode and city.',
  },
  website: {
    placeholder: 'https://www.example.com',
    helpText: 'Full address, including https://',
  },
  email: {
    placeholder: 'contact@example.com',
    helpText: 'General contact address for the company.',
  },
  phone: {
    placeholder: 'e.g. +41 44 123 45 67',
    helpText: 'Include the country code.',
  },
};

/** A field's own hints, as stored — either may be absent. */
export interface FieldHints {
  placeholder?: string;
  helpText?: string;
}

/**
 * The hints to render for a field: its OWN values win, and a slot default fills the gaps.
 *
 * Structurally typed so it accepts both the builder's `MarkedVariable` and the fill flow's
 * `FillVariable` without either importing the other. A plain field (no slot) is returned
 * unchanged — its hints are authored, and an empty one means the author wanted none.
 */
export function resolveFieldHints(field: {
  placeholder?: string | null;
  helpText?: string | null;
  subKind?: string | null;
  companySubKind?: string | null;
}): FieldHints {
  const slot = field.subKind ?? field.companySubKind;
  const defaults = field.subKind
    ? PERSON_HINTS[slot ?? '']
    : field.companySubKind
      ? COMPANY_HINTS[slot ?? '']
      : undefined;

  return {
    placeholder: field.placeholder || defaults?.placeholder || undefined,
    helpText: field.helpText || defaults?.helpText || undefined,
  };
}
